import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * A prompt the user dismisses closes at once and is never held shut behind its save; a save that
 * fails brings that one prompt back with the reason. Tracked per key, from every attempt's own
 * result, so a later dismissal of another key cannot hide an earlier failure.
 */
export function useOptimisticDismissal<K>(save: (key: K) => Promise<unknown>, onSaved?: () => void): {
  readonly hidden: (key: K) => boolean;
  readonly dismiss: (key: K) => void;
  readonly failureOf: (key: K) => { readonly error: unknown };
} {
  const [hiddenKeys, setHiddenKeys] = useState<ReadonlySet<K>>(new Set());
  const [failures, setFailures] = useState<ReadonlyMap<K, unknown>>(new Map());
  const mutation = useMutation({
    mutationFn: save,
    onSuccess: () => onSaved?.(),
    onError: (error, key) => {
      setHiddenKeys((prev) => new Set([...prev].filter((k) => k !== key)));
      setFailures((prev) => new Map(prev).set(key, error));
    },
  });
  return {
    hidden: (key) => hiddenKeys.has(key),
    dismiss: (key) => {
      setFailures((prev) => { const next = new Map(prev); next.delete(key); return next; });
      setHiddenKeys((prev) => new Set([...prev, key]));
      mutation.mutate(key);
    },
    failureOf: (key) => ({ error: failures.get(key) ?? null }),
  };
}
